import type { WrittenPage } from '../sections'
import { LINKS } from './links'

/**
 * Strategy Consulting — Halil's page 2. Covers DAIMI, AIMI and Strategy Advisory
 * as one flowing page with no sub-menu, as specified.
 *
 * This is the one solution page that keeps the "How we work" section. The copy
 * document repeats it on all three, and read in sequence it goes flat — so it
 * stays here, where the Discovery Platform is the concrete proof of it, and the
 * other two pages carry the same idea in one line inside their closing. That is a
 * structural edit, not a claim edit; `docs/website/COPY-REVIEW.md` records it.
 */
/** The Impact Platform itself — where the hero's "Get Started" sends people. */
const PLATFORM = 'https://impact.digitopia.ai'

export const strategyConsulting: WrittenPage = {
  navLabel: 'Strategy Consulting',
  title: 'Strategy Consulting: DAIMI and AIMI',
  description:
    'Establish a defensible baseline, prioritize what creates value, and turn maturity progress into P&L impact. DAIMI, AIMI and Strategy Advisory.',
  sections: [
    {
      kind: 'hero',
      headline: 'Solve the priorities fight.',
      sub: 'Strategize and prioritize your agentic AI journey. We are the global leader in maturity assessment and in converting maturity progress into business value, with 3,000+ assessments across 40+ countries.',
      photo: {
        src: '/photo/boardroom-daimi-tablet.jpg',
        alt: 'A Digitopia consultant with a tablet presenting a DAIMI scorecard, six dimensions scored against a benchmark, to three executives.',
      },
      ctas: [
        { label: 'Book a Call', href: '#contact' },
        { label: 'Get Started', href: PLATFORM },
      ],
      check:
        '"Global leader in maturity assessment" is the strongest claim on the site. It is ' +
        'currently substantiated inline with the measurement count, which is the option ' +
        'your document offered. Confirm that is enough for the markets you publish in, or ' +
        'soften it.',
    },
    {
      kind: 'prose',
      align: 'center',
      size: 'lg',
      tightTop: true,
      paragraphs: [
        "Twelve executives arrive with twelve priority lists, each defensible on its own terms. Everyone agrees AI matters. Nobody agrees what comes first. Every one has a business case, a sponsor, and a reason it cannot wait. Without a shared fact base, the loudest voice wins. That's not what you deserve.",
        'An assessment earns its keep when it answers three questions your executive team can act on. Where are we today. Where do we need to be. What has to change first.',
      ],
    },
    {
      kind: 'features',
      heading: 'What you get',
      columns: 3,
      items: [
        {
          title: 'Scorecard & Benchmark',
          body: 'Six dimensions, benchmarked against your sector and the global set. The debate shifts from opinion to evidence.',
        },
        {
          title: 'Strategy & Roadmap',
          body: 'Sequenced by value and by the change capacity you actually have, so low-value work gets stopped rather than quietly deprioritized.',
        },
        {
          title: 'Business Value Correlation',
          body: 'Every point of maturity mapped to the KPI and the P&L line it moves.',
        },
      ],
    },
    {
      kind: 'scale',
      heading: 'DAIMI. The Digital & AI Maturity Index.',
      sub: 'The full enterprise view.',
      intro:
        'Six dimensions: Customer, Operations, Innovation, People, Governance and Technology. Five levels, from beginner to digital. Benchmarked against 3,000+ measurements across 40+ countries and 20+ industries.',
      // The wheel stands in for the ladder, in the band itself — asked for on
      // 2026-08-28: "bu gri kısma DAIMI Index görselini almak yeterli". The five
      // levels were written out under a drawing that already names them, and the
      // drawing also carries the six dimensions the intro lists.
      photo: {
        src: '/photo/daimi-framework-wheel-labelled.png',
        alt: 'The DAIMI wheel, showing Customer, Operations, Innovation, People, Governance and Technology around the Digital & AI Maturity Index, with the sub-dimensions measured under each.',
      },
      caption: 'The six DAIMI dimensions and what is measured under each.',
      cta: { label: 'Explore DAIMI', href: LINKS.daimi },
      check:
        'Your document quotes $15.2M of value per 0.1 DAIMI point as the proof line for ' +
        'this section, and it is not on the page. The coefficient comes from a single $1B ' +
        'manufacturer analysis, and a number that precise on a public page invites a ' +
        'challenge just as precise. Either publish it with the basis stated in the same ' +
        'breath, or hold it until there is a broader one. Restoring it is one line.',
    },
    {
      // The heading the calculator never had. It arrives straight after the
      // DAIMI band with no title of its own, so the first thing a reader meets
      // is a revenue field with no stated reason to fill it in. Asked for on
      // 2026-09-01: a line that says what the widget is for, in DAIMI's terms.
      kind: 'prose',
      heading: 'See DAIMI’s effect in your own numbers',
      paragraphs: [
        'Four inputs — your revenue, your country, your industry and what you are trying to achieve — turn the maturity argument above into a figure you can take into a board meeting.',
      ],
    },
    {
      /*
       * The ROI calculator, immediately under the DAIMI band.
       *
       * Halil, on Teams, 2026-08-29: *"Bunu da Strategy Consulting sayfamızda
       * DAIMI çözümünü anlattığımız bölümün hemen altına koyalım derim."* Here
       * rather than at the foot of the page because the DAIMI band is the one
       * that claims maturity moves the P&L, and this is the reader's chance to
       * put their own revenue into that claim while it is still on screen. Its
       * own page is `/tools/roi-calculator`; this is the second placement, not
       * a duplicate of the copy — the widget is one component.
       */
      kind: 'tool',
      tool: 'roi-calculator',
    },
    {
      // After the five levels and before AIMI narrows the question: the score
      // being read by the people who have to act on it.
      kind: 'photo',
      photo: {
        src: '/photo/daimi-score-presentation.jpg',
        alt: 'A Digitopia consultant presenting a DAIMI score of 3.4 against a 3.7 target, dimension by dimension, to two executives.',
      },
    },
    {
      // The same shape as DAIMI above, asked for in the same breath: one band,
      // the argument and then the wheel that carries it.
      kind: 'scale',
      heading: 'AIMI. The AI Maturity Index.',
      sub: 'When the question is narrower and more urgent.',
      intro:
        'AI maturity is not about models. It depends on data foundations, workflow design, human judgment, governance, measurement discipline and scalable architecture. AIMI measures exactly those, so you find out whether your organization can carry the AI ambition it has already announced.',
      pullquote: 'Wrapping AI around chaos does not create innovation. It creates liability.',
      photo: {
        src: '/photo/aimi-framework-wheel.webp',
        alt: 'The AIMI wheel, showing Capabilities, Governance, Technology, Customer, Operations and Administration around the Artificial Intelligence Maturity Index, with the sub-dimensions measured under each.',
      },
      caption: 'The six AIMI dimensions and what is measured under each.',
      // The DAIMI band above ends on "Explore DAIMI"; this one showed the AIMI
      // wheel and then left the reader with nowhere to go. Same button, same
      // place, pointing at the AIMI solution page.
      cta: { label: 'Explore AIMI', href: LINKS.aimi },
      check:
        'The AIMI wheel names its six dimensions as Capabilities, Governance, Technology, Customer, Operations and Administration, which is not the set the paragraph above describes and not DAIMI’s six either. The drawing is the supplied artwork and has been left as it is — worth a sentence of copy naming the six, if AIMI’s dimensions are meant to be read here rather than only seen.',
    },
    {
      kind: 'list',
      heading: 'Strategy Advisory. The number is where the work starts.',
      intro:
        'A score changes nothing on its own. Our consultants stay in the room for the decisions that follow: what to stop, what to sequence, who owns which outcome, and what the board sees each quarter.',
      items: [
        'Target state definition, agreed by the executive team rather than presented to it',
        'Investment cases tied to specific KPIs and P&L lines',
        'Operating model and governance choices',
        'Executive alignment sessions, including the uncomfortable ones',
        'Annual reassessment and quarterly business reviews',
      ],
      cta: { label: 'Book a Call', href: '#contact' },
    },
    {
      kind: 'features',
      heading: 'How we work',
      columns: 2,
      // Two columns of `text-base` muted paragraphs under 15px titles read as
      // small print beside the rest of the page; `textSize: 'lg'` is the opt-in
      // the grid already carries for exactly that.
      textSize: 'lg',
      items: [
        {
          title: 'Human expertise',
          body: 'Senior consultants run interactive, challenge-based sessions. No self-scored questionnaires. We intervene when a team is being generous to itself, and when it is being unfair.',
        },
        {
          title: 'Agentic execution',
          body: 'The Discovery Platform is the automation engine underneath. Faster, better, more affordable, fully digital and AI-powered.',
        },
      ],
    },
    {
      // Two bullet points under the word "Proof" were the weakest thing on the
      // page — asked for on 2026-08-28: *"bu kısmı çok acemi ve ucuz duruyor…
      // hall of fame template ine benzer ama minimal bir tasarım… kaydırmalı
      // waikiki ve brisa olur, read more a basınca ilgili sayfaya gider"*. So
      // proof is now the customers saying it themselves, in the Hall of Fame's
      // card pared back and behind a tab per company.
      //
      // Both quotes and both attributions are the ones already on the Hall of
      // Fame cards, so a reader who follows either button meets the same words
      // under the same name. LC Waikiki's is the *first* of Şerafettin Özer's
      // three paragraphs — the one about prioritization and alignment across
      // business units, which is this page's subject — and deliberately not the
      // two the home page carries, so the two pages do not print the same
      // extract twice.
      kind: 'quotes',
      variant: 'minimal-carousel',
      // "Success Stories" rather than "Proof" (2026-08-31) — the tabs are the
      // customers' own names and the section's own button already says success
      // stories, so the heading now names what the reader is looking at.
      // Three more tabs the same day (*"3 örnek daha varsa ekleyebilirsin"*):
      // Kordsa, TEMSA and Akçansa, each the words, mark and portrait
      // `content/testimonials/` already carries, so no page can drift from the
      // Hall of Fame. All three are about strategy rather than the assessment
      // mechanics — roadmaps, aligning digital investment with outcomes, and
      // cross-functional prioritisation — which is what this page argues.
      heading: 'Success Stories',
      items: [
        {
          company: 'LC Waikiki',
          logo: { name: 'LC Waikiki', src: '/logo/lcwaikikicard.png' },
          portrait: { src: '/photo/portrait-serafettin-ozer.png', alt: 'Şerafettin Özer' },
          quote: [
            'Strategic prioritization has been a key focus for us since 2021. With varying '
            + 'maturity levels across our business units (retail, logistics, and e-commerce), '
            + 'alignment is crucial. Conducting the assessment with full transparency helps us '
            + 'stay on track and make informed decisions.',
          ],
          source: 'Şerafettin Özer, Chief Digital and Information Officer',
          cta: { label: 'Read more', href: '/en/customers/lc-waikiki' },
        },
        {
          company: 'Brisa',
          logo: { name: 'Brisa', src: '/logo/brisacard.png' },
          portrait: { src: '/photo/portrait-filiz-mert.png', alt: 'Filiz Mert' },
          quote: [
            'We have been renewing the digital transformation solution on a regular basis for 3 '
            + 'years. This year, we plan to include more Digital Maturity Index (DMI) assessment '
            + 'results in our action plan by pulling this study earlier in order to evaluate DMI '
            + 'outputs and determine company strategies according to these outputs at our '
            + 'strategy meeting. We will continue to renew DMI to keep seeing where our '
            + 'strengths lie and also the muscles we need to strengthen, and what we have '
            + 'achieved in the last 3 years in our digital transformation journey.',
          ],
          source: 'Filiz Mert, Digital Projects & Analytics Manager',
          cta: { label: 'Read more', href: '/en/customers/brisa' },
        },
        {
          company: 'Kordsa',
          logo: { name: 'Kordsa', src: '/logo/kordsacard.png' },
          portrait: { src: '/photo/portrait-fatih-akar.png', alt: 'Fatih Akar' },
          quote: [
            'Digitopia’s DMI assessments have helped us create clear, actionable digital '
            + 'roadmaps while staying on track. Their insights have been invaluable, guiding us '
            + 'to focus on critical areas like AI long before it became a major topic.',
            'Digitopia is not just a partner. They play an essential role in shaping and '
            + 'driving our digital strategy.',
          ],
          source: 'Fatih Akar, Director of Global Information Technologies',
          cta: { label: 'Read more', href: '/en/customers/kordsa' },
        },
        {
          company: 'TEMSA',
          logo: { name: 'TEMSA', src: '/logo/temsacard.png' },
          portrait: { src: '/photo/portrait-erdinc-ugurlu.png', alt: 'Erdinç Uğurlu' },
          quote: [
            'It provided us with a clear, objective view of where we stand and how we compare, '
            + 'both within our holding and across the industry. More importantly, it enabled us '
            + 'to track progress over time and align our digital investments with measurable '
            + 'outcomes. This structured approach brings clarity, focus, and confidence to our '
            + 'transformation efforts.',
          ],
          source: 'Erdinç Uğurlu, CIO, TEMSA',
          cta: { label: 'Read more', href: '/en/customers/temsa' },
        },
        {
          company: 'Akçansa',
          logo: { name: 'Akçansa', src: '/logo/akcansacard.png' },
          portrait: { src: '/photo/portrait-aydin-sener.png', alt: 'Aydın Şener' },
          quote: [
            'Using Digitopia’s tools has been incredibly valuable for us. DMI and Impact '
            + 'Platform helped us achieve a clear, structured roadmap for digital '
            + 'transformation. By engaging cross-functional teams from HR, sales, and IT, we '
            + 'aligned on priorities, sparked company-wide awareness, and identified key '
            + 'projects to drive change effectively.',
          ],
          source: 'Aydın Şener, Digital Transformation and Industry 4.0 Manager',
          cta: { label: 'Read more', href: '/en/customers/akcansa' },
        },
      ],
      cta: { label: 'See success stories', href: LINKS.successStories },
      check:
        'Redesigning this section replaced the two proof claims with the two customers’ own ' +
        'words, so three assertions are no longer on the page: that LC Waikiki made its annual ' +
        'DAIMI score a company OKR, that Brisa moved 3.1 to 3.8 over four years, and Brisa’s ' +
        'ISO 42001 first-in-Türkiye certification. None of them is in either quote. Say if ' +
        'you want them back as a line above the cards. Still open from before: a third proof ' +
        'point is written and withheld — TOGG used DAIMI to build a common fact base, then ' +
        'sharpened priorities and sequencing through annual reassessment and quarterly reviews. ' +
        'Confirm TOGG can be named publicly and it becomes a third tab.',
    },
    /*
      No closing dark CTA band here, asked for 2026-08-30: "form olan herhangi
      bir sayfada formun hemen üstünde genelde bu siyah kutu oluyor, bunlar
      olmasın". The closing form (`components/ContactBand.tsx`) already sits at
      the foot of this page, so a band whose buttons pointed at `#contact` was
      asking the reader to scroll to something they could already see.
    */
  ],
}
